import { 
  DollarOutlined, 
  CreditCardOutlined, 
  MessageOutlined, 
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from '@umijs/max';
import { useIntl } from 'umi';
import { message, Tooltip } from 'antd';
import React, { useState, useEffect, useRef } from 'react';
import {
  getUnreadCount,
  onInquiryMessageReceived,
  onNewInquiry,
  getSocket,
  getGeneralInquiryUnreadCount,
  onGeneralInquiryMessageReceived,
  onGeneralInquiryNew,
  onDepositRequestNew,
  onDepositRequestResolved,
  requestAdminPendingCounts,
  onAdminPendingCounts,
  offAdminPendingCounts,
  type AdminPendingCounts,
} from '@/utils/socket';

/**
 * Defer non-critical socket follow-up requests until the browser is idle (capped),
 * so the main thread and first API round-trips stay ahead on high-latency networks.
 */
function scheduleDeferredWork(run: () => void, idleTimeoutMs: number): () => void {
  if (typeof requestIdleCallback !== 'undefined') {
    const id = requestIdleCallback(() => run(), { timeout: idleTimeoutMs });
    return () => cancelIdleCallback(id);
  }
  const tid = window.setTimeout(run, 200);
  return () => window.clearTimeout(tid);
}

export const NotificationIcon: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const routeContext: 'todaymall' | 'todayggigu' | 'all' = location.pathname.startsWith('/todaymall')
    ? 'todaymall'
    : location.pathname.startsWith('/todayggigu')
      ? 'todayggigu'
      : 'all';

  // Todaymall counts
  const [orderInquiryCountMall, setOrderInquiryCountMall] = useState(0);
  const [generalInquiryCountMall, setGeneralInquiryCountMall] = useState(0);
  const [depositApplicationCountMall, setDepositApplicationCountMall] = useState(0);
  const [bankTransferPaymentCountMall, setBankTransferPaymentCountMall] = useState(0);
  
  // Todayggigu counts
  const [orderInquiryCountGgigu] = useState(0); // TODO: Implement API
  const [generalInquiryCountGgigu] = useState(0); // TODO: Implement API
  const [depositApplicationCountGgigu, setDepositApplicationCountGgigu] = useState(0);
  const [bankTransferPaymentCountGgigu] = useState(0); // TODO: Implement API
  const [inventoryInquiryCountGgigu] = useState(0); // TODO: Implement API
  
  // Track processed message/inquiry IDs to prevent double counting
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  const processedInquiryIdsRef = useRef<Set<string>>(new Set());
  /** Browser `window.setTimeout` ids are numbers; Node typings use `Timeout` — avoid mixing them. */
  const reconnectPendingDebounceRef = useRef<number | null>(null);
  const reconnectListenerBoundRef = useRef(false);

  // Fetch inquiry counts on mount and when socket is ready.
  // Socket init is deferred (~350ms) in SocketInitializer, so all listeners
  // must register only after getSocket() returns a live instance — otherwise
  // on* helpers no-op and badges stay at 0 forever.
  useEffect(() => {
    let cancelDeferredFetch: (() => void) | undefined;
    let cancelRetryTimer: number | null = null;
    let waitForSocketCancelled = false;
    let listenersBound = false;

    const pendingCountsHandler = (data: AdminPendingCounts) => {
      setBankTransferPaymentCountMall(data.bankTransferPendingCount);
      const depositPending =
        data.depositRequestPendingCount ?? data.depositRechargePendingCount ?? 0;
      setDepositApplicationCountMall(depositPending);
    };

    const fetchCounts = () => {
      getUnreadCount(
        (data: any) => {
          setOrderInquiryCountMall(data?.count || 0);
        },
        () => {}
      );

      getGeneralInquiryUnreadCount(
        (data: any) => {
          setGeneralInquiryCountMall(data?.count || 0);
        },
        () => {}
      );

      requestAdminPendingCounts();
    };

    const scheduleFetchCounts = () => {
      cancelDeferredFetch?.();
      cancelDeferredFetch = scheduleDeferredWork(fetchCounts, 2000);
    };

    const onReconnectPendingCounts = () => {
      if (reconnectPendingDebounceRef.current !== null) {
        window.clearTimeout(reconnectPendingDebounceRef.current);
      }
      reconnectPendingDebounceRef.current = window.setTimeout(() => {
        reconnectPendingDebounceRef.current = null;
        requestAdminPendingCounts();
        getUnreadCount(
          (data: any) => setOrderInquiryCountMall(data?.count || 0),
          () => {}
        );
        getGeneralInquiryUnreadCount(
          (data: any) => setGeneralInquiryCountMall(data?.count || 0),
          () => {}
        );
      }, 700);
    };

    const orderMessageHandler = (data: any) => {
      const messageId = data?.message?._id || data?.messageId || `${data?.inquiryId}_${Date.now()}`;

      if (processedMessageIdsRef.current.has(messageId)) {
        return;
      }

      processedMessageIdsRef.current.add(messageId);
      if (routeContext !== 'todayggigu') {
        message.info(intl.formatMessage({ id: 'pages.notification.orderInquiryMessage' }));
      }

      getUnreadCount(
        (countData: any) => {
          setOrderInquiryCountMall(countData?.count || 0);
        },
        () => {}
      );

      setTimeout(() => {
        processedMessageIdsRef.current.delete(messageId);
      }, 5000);
    };

    const orderInquiryHandler = (data: any) => {
      const inquiryId = data?.inquiry?._id || data?.inquiryId || data?._id;

      if (processedInquiryIdsRef.current.has(inquiryId)) {
        return;
      }

      processedInquiryIdsRef.current.add(inquiryId);

      getUnreadCount(
        (countData: any) => {
          setOrderInquiryCountMall(countData?.count || 0);
        },
        () => {}
      );

      setTimeout(() => {
        processedInquiryIdsRef.current.delete(inquiryId);
      }, 5000);
    };

    const generalMessageHandler = () => {
      getGeneralInquiryUnreadCount(
        (countData: any) => {
          setGeneralInquiryCountMall(countData?.count || 0);
          if (routeContext !== 'todayggigu') {
            message.info(intl.formatMessage({ id: 'pages.notification.generalInquiryMessage' }));
          }
        },
        () => {}
      );
    };

    const generalInquiryHandler = () => {
      getGeneralInquiryUnreadCount(
        (countData: any) => {
          setGeneralInquiryCountMall(countData?.count || 0);
        },
        () => {}
      );
    };

    // Toast only — authoritative counts come from admin:pending-counts:response
    const depositRequestNewHandler = () => {
      if (routeContext !== 'todayggigu') {
        message.info(intl.formatMessage({ id: 'pages.notification.depositApplication' }));
      }
    };

    const depositRequestResolvedHandler = (data: any) => {
      if (routeContext !== 'todayggigu') {
        message.success(
          `${intl.formatMessage({ id: 'pages.notification.depositApplication' })} resolved (${data?.outcome || 'unknown'})`,
          3
        );
      }
    };

    // A thread left/re-entered 미확인 (admin replied, or 확인 / 처리중 pressed —
    // possibly by another admin). No toast: nothing new arrived for this admin.
    const orderCountChangedHandler = () => {
      getUnreadCount(
        (countData: any) => setOrderInquiryCountMall(countData?.count || 0),
        () => {}
      );
    };

    const generalCountChangedHandler = () => {
      getGeneralInquiryUnreadCount(
        (countData: any) => setGeneralInquiryCountMall(countData?.count || 0),
        () => {}
      );
    };

    const markReadHandler = () => {
      getUnreadCount(
        (countData: any) => {
          setOrderInquiryCountMall(countData?.count || 0);
        },
        () => {}
      );
    };

    const generalMarkReadHandler = () => {
      getGeneralInquiryUnreadCount(
        (countData: any) => {
          setGeneralInquiryCountMall(countData?.count || 0);
        },
        () => {}
      );
    };

    const bindListeners = (sock: NonNullable<ReturnType<typeof getSocket>>) => {
      if (listenersBound) return;
      listenersBound = true;

      onAdminPendingCounts(pendingCountsHandler);
      onInquiryMessageReceived(orderMessageHandler);
      onNewInquiry(orderInquiryHandler);
      onGeneralInquiryMessageReceived(generalMessageHandler);
      onGeneralInquiryNew(generalInquiryHandler);
      onDepositRequestNew(depositRequestNewHandler);
      onDepositRequestResolved(depositRequestResolvedHandler);

      sock.on('admin:inquiry:mark-read:success', markReadHandler);
      sock.on('admin:general-inquiry:mark-read:success', generalMarkReadHandler);

      sock.on('admin:inquiry:unread-count:changed', orderCountChangedHandler);
      sock.on('admin:inquiry:updated', orderCountChangedHandler);
      sock.on('admin:general-inquiry:unread-count:changed', generalCountChangedHandler);
      sock.on('admin:general-inquiry:updated', generalCountChangedHandler);

      if (!reconnectListenerBoundRef.current) {
        sock.on('connect', onReconnectPendingCounts);
        reconnectListenerBoundRef.current = true;
      }
    };

    const attachWhenSocketReady = () => {
      if (waitForSocketCancelled) return;
      const sock = getSocket();
      if (!sock) {
        cancelRetryTimer = window.setTimeout(attachWhenSocketReady, 80);
        return;
      }

      bindListeners(sock);

      if (sock.connected) {
        scheduleFetchCounts();
      } else {
        sock.once('connect', scheduleFetchCounts);
      }
    };
    attachWhenSocketReady();

    return () => {
      waitForSocketCancelled = true;
      cancelDeferredFetch?.();
      if (cancelRetryTimer !== null) {
        window.clearTimeout(cancelRetryTimer);
        cancelRetryTimer = null;
      }
      if (reconnectPendingDebounceRef.current !== null) {
        window.clearTimeout(reconnectPendingDebounceRef.current);
        reconnectPendingDebounceRef.current = null;
      }

      const socket = getSocket();
      if (socket) {
        socket.off('connect', onReconnectPendingCounts);
        socket.off('connect', scheduleFetchCounts);
        socket.off('admin:inquiry:message:received', orderMessageHandler);
        socket.off('admin:inquiry:new', orderInquiryHandler);
        socket.off('admin:general-inquiry:message:received', generalMessageHandler);
        socket.off('admin:general-inquiry:new', generalInquiryHandler);
        socket.off('admin:deposit-request:new', depositRequestNewHandler);
        socket.off('deposit-request:new', depositRequestNewHandler);
        socket.off('admin:deposit-request:resolved', depositRequestResolvedHandler);
        socket.off('deposit-request:resolved', depositRequestResolvedHandler);
        socket.off('admin:inquiry:mark-read:success', markReadHandler);
        socket.off('admin:general-inquiry:mark-read:success', generalMarkReadHandler);
        socket.off('admin:inquiry:unread-count:changed', orderCountChangedHandler);
        socket.off('admin:inquiry:updated', orderCountChangedHandler);
        socket.off('admin:general-inquiry:unread-count:changed', generalCountChangedHandler);
        socket.off('admin:general-inquiry:updated', generalCountChangedHandler);
      }
      offAdminPendingCounts(pendingCountsHandler);
      reconnectListenerBoundRef.current = false;
      listenersBound = false;
    };
  }, [intl, routeContext]);

  const notificationItems = [
    {
      key: 'deposit',
      icon: <DollarOutlined />,
      label: intl.formatMessage({ id: 'pages.notification.depositApplication' }),
      mallCount: depositApplicationCountMall,
      ggiguCount: depositApplicationCountGgigu,
      mallPath: '/settlement/deposit-application-history',
      // ggiguPath: '/todayggigu/members/deposit-application-detailed',
      color: '#52c41a',
    },
    {
      key: 'bank-transfer',
      icon: <CreditCardOutlined />,
      label: intl.formatMessage({ id: 'pages.notification.bankTransferPayment' }),
      mallCount: bankTransferPaymentCountMall,
      ggiguCount: bankTransferPaymentCountGgigu,
      mallPath: '/settlement/pending-payment',
      // ggiguPath: '/todayggigu/orders/business/pending-payment',
      color: '#1890ff',
    },
    {
      key: 'general-inquiry',
      icon: <MessageOutlined />,
      label: intl.formatMessage({ id: 'pages.notification.generalInquiries' }),
      mallCount: generalInquiryCountMall,
      ggiguCount: generalInquiryCountGgigu,
      mallPath: '/cs/inquiries',
      // ggiguPath: '/todayggigu/cs/inquiries',
      color: '#722ed1',
    },
    {
      key: 'order-inquiry',
      icon: <QuestionCircleOutlined />,
      label: intl.formatMessage({ id: 'pages.notification.orderInquiries' }),
      mallCount: orderInquiryCountMall,
      ggiguCount: orderInquiryCountGgigu,
      mallPath: '/orders/questions-detailed',
      // ggiguPath: '/todayggigu/orders/questions-detailed',
      color: '#fa8c16',
    },
    // {
    //   key: 'inventory',
    //   icon: <InboxOutlined />,
    //   label: intl.formatMessage({ id: 'pages.notification.inventoryInquiries' }),
    //   mallCount: 0,
    //   ggiguCount: inventoryInquiryCountGgigu,
    //   mallPath: null,
    //   ggiguPath: '/todayggigu/orders/inventory-questions-detailed',
    //   color: '#eb2f96',
    // },
  ];

  return (
    <>
      <style>
        {`
          @keyframes adminHeaderNotifyCountShake {
            0%, 100% { transform: translateX(0) rotate(0deg) scale(1); }
            8% { transform: translateX(-4px) rotate(-16deg) scale(1.2); }
            16% { transform: translateX(4px) rotate(16deg) scale(1.22); }
            24% { transform: translateX(-4px) rotate(-14deg) scale(1.2); }
            32% { transform: translateX(4px) rotate(14deg) scale(1.18); }
            40% { transform: translateX(-3px) rotate(-10deg) scale(1.12); }
            48% { transform: translateX(3px) rotate(10deg) scale(1.1); }
            56% { transform: translateX(-2px) rotate(-6deg) scale(1.06); }
            64% { transform: translateX(2px) rotate(6deg) scale(1.04); }
            72%, 88% { transform: translateX(0) rotate(0deg) scale(1.02); }
          }
          .admin-header-notify-count--pulse {
            display: inline-block;
            animation: adminHeaderNotifyCountShake 1.35s ease-in-out infinite;
            transform-origin: center center;
          }
          @media (prefers-reduced-motion: reduce) {
            .admin-header-notify-count--pulse {
              animation: adminHeaderNotifyCountPulseReduced 2s ease-in-out infinite;
            }
          }
          @keyframes adminHeaderNotifyCountPulseReduced {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.85; transform: scale(1.06); }
          }
        `}
      </style>

      {/* Tight cluster: read as one “notifications” group; outer spacing is in app.tsx */}
      <div className="admin-header-notify-cluster" style={{ display: 'inline-flex', alignItems: 'center', gap: 20 }}>
        {notificationItems
          .filter((item) => {
            if (routeContext === 'todaymall') return item.mallPath !== null;
            return true;
          })
          .map((item) => {
            const displayCount =
              routeContext === 'todaymall'
                ? item.mallCount
                : routeContext === 'todayggigu'
                  ? item.ggiguCount
                  : item.mallCount + item.ggiguCount;
            const hasNotifications = displayCount > 0;
            const countLabel =
              displayCount > 999 ? '999+' : String(displayCount);

            return (
              <Tooltip key={item.key} title={item.label}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`${item.label}: ${displayCount}`}
                  onClick={() => item.mallPath && navigate(item.mallPath)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      item.mallPath && navigate(item.mallPath);
                    }
                  }}
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    cursor: item.mallPath ? 'pointer' : 'default',
                  }}
                >
                  <span
                    style={{
                      fontSize: 26,
                      lineHeight: 1,
                      color: hasNotifications ? item.color : '#999',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </span>
                  <span
                    style={{
                      position: 'absolute',
                      top: -3,
                      right: -7,
                      minWidth: 18,
                      height: 18,
                      padding: countLabel.length > 2 ? '0 4px' : '0 5px',
                      borderRadius: 9,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: '18px',
                      textAlign: 'center',
                      color: '#fff',
                      background: hasNotifications ? item.color : '#bfbfbf',
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                    }}
                  >
                    <span className={hasNotifications ? 'admin-header-notify-count--pulse' : undefined}>
                      {countLabel}
                    </span>
                  </span>
                </div>
              </Tooltip>
            );
          })}
      </div>
    </>
  );
};
